/**
 * ConfirmProvider — reemplaza window.confirm/window.prompt con diálogos
 * Radix accesibles (focus trap, Esc, aria-modal) manteniendo la ergonomía
 * de una guarda imperativa de una línea dentro de handlers async.
 *
 * Ver docs/p2-dialogs-design.md §1-§2 (arquitecto-app) para el diseño completo.
 *
 * Uso:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: t("x.title"), description: t("x.desc"), destructive: true }))) return;
 *
 *   const prompt = usePrompt();
 *   const value = await prompt({ title: t("x.title"), multiline: true });
 *   if (value === null) return; // cancelado
 *
 * Retorno de foco al cerrar (WCAG 2.4.3): por default se restaura el foco en
 * `document.activeElement` capturado al invocar confirm()/prompt() (el
 * disparador). Cuando el disparador es un elemento EFÍMERO que se desmonta
 * al abrir el diálogo (p. ej. un <DropdownMenuItem> dentro de un menú "…"
 * que Radix cierra y desmonta al instante), pasa `returnFocusTo` apuntando
 * al elemento persistente al que debe volver el foco (el botón "…" que abre
 * el menú), como elemento DOM, ref de React ({ current }) o selector CSS:
 *   if (!(await confirm({ ..., returnFocusTo: menuTriggerEl }))) return;
 */
import { createContext, useCallback, useContext, useRef, useState } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { Button, buttonVariants } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const ConfirmContext = createContext(null);

// Normaliza `returnFocusTo` (elemento DOM | ref de React | selector CSS) a un
// elemento DOM enfocable, o null si no se puede resolver.
const resolveFocusTarget = (target) => {
  if (!target) return null;
  if (typeof target === "string") return document.querySelector(target);
  if (target instanceof Element) return target;
  if (target.current instanceof Element) return target.current;
  return null;
};

const CLOSED_CONFIRM = {
  open: false,
  title: "",
  description: null,
  confirmLabel: null,
  cancelLabel: null,
  destructive: false,
};

const CLOSED_PROMPT = {
  open: false,
  title: "",
  description: null,
  label: null,
  placeholder: "",
  multiline: false,
  required: false,
  confirmLabel: null,
  value: "",
};

export const ConfirmProvider = ({ children }) => {
  const { t } = useI18n();
  const [confirmState, setConfirmState] = useState(CLOSED_CONFIRM);
  const [promptState, setPromptState] = useState(CLOSED_PROMPT);
  // Un único diálogo de cada tipo, reusado secuencialmente (§1 — sin caso
  // concurrente en el código actual). El resolve pendiente, el elemento que
  // disparó la apertura y el `returnFocusTo` explícito (opcional) viven
  // juntos en el mismo ref, así siempre corresponden a la MISMA invocación:
  // cada confirm()/prompt() sobrescribe el ref completo con su propio trío.
  const confirmResolveRef = useRef(null); // { resolve, trigger, returnFocusTo }
  const promptResolveRef = useRef(null); // { resolve, trigger, returnFocusTo }

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      confirmResolveRef.current = {
        resolve,
        trigger: document.activeElement,
        returnFocusTo: opts.returnFocusTo ?? null,
      };
      setConfirmState({
        open: true,
        title: opts.title,
        description: opts.description ?? null,
        confirmLabel: opts.confirmLabel ?? null,
        cancelLabel: opts.cancelLabel ?? null,
        destructive: !!opts.destructive,
      });
    });
  }, []);

  const prompt = useCallback((opts) => {
    return new Promise((resolve) => {
      promptResolveRef.current = {
        resolve,
        trigger: document.activeElement,
        returnFocusTo: opts.returnFocusTo ?? null,
      };
      setPromptState({
        open: true,
        title: opts.title,
        description: opts.description ?? null,
        label: opts.label ?? null,
        placeholder: opts.placeholder ?? "",
        multiline: !!opts.multiline,
        required: !!opts.required,
        confirmLabel: opts.confirmLabel ?? null,
        value: opts.defaultValue ?? "",
      });
    });
  }, []);

  const settleConfirm = useCallback((result) => {
    const entry = confirmResolveRef.current;
    setConfirmState(CLOSED_CONFIRM);
    if (entry?.resolve) entry.resolve(result);
    // No limpiamos confirmResolveRef aquí: onCloseAutoFocus (disparado por
    // Radix después de la animación de cierre) todavía necesita `trigger`.
  }, []);

  const settlePrompt = useCallback((result) => {
    const entry = promptResolveRef.current;
    setPromptState(CLOSED_PROMPT);
    if (entry?.resolve) entry.resolve(result);
  }, []);

  // Restaura el foco en vez del default de Radix (document.body, porque no
  // usamos <AlertDialogTrigger>/<DialogTrigger> declarativos al montar el
  // diálogo imperativamente vía promesa). Prioridad:
  //   1. `returnFocusTo` explícito, si sigue conectado al DOM.
  //   2. `trigger` capturado (document.activeElement al invocar), si sigue
  //      conectado — cubre el caso común (botón persistente en la fila).
  //   3. Si ninguno sigue conectado (p. ej. el disparador era un
  //      <DropdownMenuItem> que Radix desmontó al cerrar el menú y el
  //      caller no pasó returnFocusTo), NO forzamos foco a un nodo muerto:
  //      dejamos el comportamiento default de Radix en vez de saltar a
  //      <body> nosotros mismos.
  const restoreConfirmFocus = useCallback((e) => {
    const entry = confirmResolveRef.current;
    confirmResolveRef.current = null;
    const explicit = resolveFocusTarget(entry?.returnFocusTo);
    if (explicit?.isConnected) {
      e.preventDefault();
      explicit.focus();
      return;
    }
    if (entry?.trigger?.isConnected) {
      e.preventDefault();
      entry.trigger.focus();
    }
  }, []);

  const restorePromptFocus = useCallback((e) => {
    const entry = promptResolveRef.current;
    promptResolveRef.current = null;
    const explicit = resolveFocusTarget(entry?.returnFocusTo);
    if (explicit?.isConnected) {
      e.preventDefault();
      explicit.focus();
      return;
    }
    if (entry?.trigger?.isConnected) {
      e.preventDefault();
      entry.trigger.focus();
    }
  }, []);

  const promptBlocked = promptState.required && !promptState.value.trim();

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}

      {/* Diálogo global de confirmación — reemplaza window.confirm en toda la app. */}
      <AlertDialog
        open={confirmState.open}
        onOpenChange={(open) => { if (!open) settleConfirm(false); }}
      >
        <AlertDialogContent
          onCloseAutoFocus={restoreConfirmFocus}
          data-testid="global-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState.title}</AlertDialogTitle>
            {confirmState.description && (
              <AlertDialogDescription asChild>
                <div>{confirmState.description}</div>
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settleConfirm(false)}>
              {confirmState.cancelLabel || t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settleConfirm(true)}
              className={confirmState.destructive ? buttonVariants({ variant: "destructive" }) : undefined}
              data-testid="global-confirm-action"
            >
              {confirmState.confirmLabel || t("confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo global de prompt de texto — reemplaza window.prompt en toda la app. */}
      <Dialog
        open={promptState.open}
        onOpenChange={(open) => { if (!open) settlePrompt(null); }}
      >
        <DialogContent
          onCloseAutoFocus={restorePromptFocus}
          data-testid="global-prompt-dialog"
        >
          <DialogHeader>
            <DialogTitle>{promptState.title}</DialogTitle>
            {promptState.description && (
              <DialogDescription>{promptState.description}</DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-2 py-2">
            {promptState.label && <Label>{promptState.label}</Label>}
            {promptState.multiline ? (
              <Textarea
                autoFocus
                value={promptState.value}
                placeholder={promptState.placeholder}
                onChange={(e) => setPromptState((s) => ({ ...s, value: e.target.value }))}
                rows={4}
                data-testid="global-prompt-textarea"
              />
            ) : (
              <Input
                autoFocus
                value={promptState.value}
                placeholder={promptState.placeholder}
                onChange={(e) => setPromptState((s) => ({ ...s, value: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !promptBlocked) settlePrompt(promptState.value);
                }}
                data-testid="global-prompt-input"
              />
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => settlePrompt(null)}>
              {t("cancel")}
            </Button>
            <Button
              onClick={() => settlePrompt(promptState.value)}
              disabled={promptBlocked}
              data-testid="global-prompt-confirm"
            >
              {promptState.confirmLabel || t("confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx.confirm;
};

export const usePrompt = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("usePrompt must be used within ConfirmProvider");
  return ctx.prompt;
};
